import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { Pipeline } from '@ui/__generated__/graphql'
import { Button, Dropdown } from '@ui/components/core'
import { Hierarchy1Icon } from '@ui/components/icons'
import { Spinner } from '@ui/components/loaders'
import { Organization, VerifySessionResponse } from '@ui/interfaces'
import axiosInstance from '@ui/lib/api/axiosHelper'
import { GetAllPipelinesQuery } from '@ui/loaders/workflow'
import { useAuth } from '@ui/providers/AuthProvider'
import { useInstanceStore, usePipelinesStore } from '@ui/stores'
import clsx from 'clsx'

import { UserMenu } from './components'

export const ActionBar = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const auth = useAuth()

  const searchingForPipeline = useRef<boolean>(false)

  const [otherOrganization, setOtherOrganization] = useState<Organization | null>(null)

  const { data } = GetAllPipelinesQuery()

  const { setSelectedInstanceKey } = useInstanceStore(useShallow((state) => state))
  const { pipelines, setPipelines, selectedPipeline, selectPipeline } = usePipelinesStore(useShallow((state) => state))

  useEffect(() => {
    if (data && data.allPipelineWorkflows) {
      setPipelines(data.allPipelineWorkflows)
    }
  }, [data, setPipelines, pipelines, location])

  useEffect(() => {
    axiosInstance
      .get('/v1/organizations')
      .then((response) => {
        getOrganizationName(response.data, auth.isDemoMode)
      })
      .catch((error) => {
        console.log(error)
      })
  }, [auth.isDemoMode])

  // This function is used to set the other organization to the demo organization
  // This is used to display the demo organization in the action bar and allow the user to switch
  // between the demo and non-demo organizations
  const getOrganizationName = (organizations: Organization[], isDemo?: boolean) => {
    const demoOrganization = organizations.find((org) => org.slug === 'datable-demo')
    const nonDemoOrganization = organizations.find((org) => org.slug !== 'datable-demo')

    if (isDemo && nonDemoOrganization) {
      setOtherOrganization(nonDemoOrganization)
    } else if (demoOrganization) {
      setOtherOrganization(demoOrganization)
    } else {
      setOtherOrganization(null)
    }
  }

  const handleOrganizationChange = async (newOrganization: Organization) => {
    auth.setOrganization(newOrganization.id, (err, response?: VerifySessionResponse) => {
      if (err) {
        console.error('Error setting organization:', err)
        return
      }

      if (!response) {
        console.error('No response from setting organization')
        return
      }

      const { instance } = response

      if (instance) {
        setSelectedInstanceKey(instance.connect_key)
        navigate(`/instances/${instance.connect_key}/explore/events`)
      }
    })
  }

  const handlePipelineSelection = (pipeline: Pipeline) => {
    selectPipeline(pipeline)
  }

  const pipelineOptions = useMemo(() => {
    return pipelines.map((pipeline) => ({ label: pipeline.name, value: pipeline }))
  }, [pipelines])

  const selectedPipelineOption = useMemo(() => {
    searchingForPipeline.current = true
    if (!selectedPipeline) {
      searchingForPipeline.current = false
      return null
    }

    searchingForPipeline.current = false
    return pipelineOptions.find((option) => option.value.id === selectedPipeline.id)?.value
  }, [pipelineOptions, selectedPipeline])

  const renderActionBar = () => {
    if (auth.isDemoMode) {
      return (
        <header className="left-0 top-0 z-20 h-16 w-full border-b border-b-slate-200 bg-primary-500 text-black">
          <nav className="flex items-center justify-between p-3" aria-label="Global">
            <div className="flex w-full items-center">
              <p className="text-sm text-white">
                You are currently in the <span className="font-bold">Sandbox environment</span>. Some features are
                limited.
              </p>
            </div>

            <div className="flex w-full items-center justify-end gap-2">
              {otherOrganization && otherOrganization.slug !== 'datable-demo' && (
                <Button
                  onClick={() => {
                    handleOrganizationChange(otherOrganization)
                  }}
                  size="small"
                  variant="secondary"
                  className="border-white text-white hover:border-gray-300 hover:text-gray-300">
                  Go to {otherOrganization.name}
                </Button>
              )}
              <Button
                size="small"
                variant="tertiary"
                onClick={() => {
                  window.open('https://app.apollo.io/#/meet/jim_ettig_bfc/30-min', '_blank', 'noreferrer')
                }}>
                Talk to an Expert
              </Button>
            </div>
          </nav>
        </header>
      )
    } else {
      return (
        <header className="left-0 top-0 z-20 h-fit w-full border-b border-b-slate-200 bg-white px-4 py-2 text-black">
          <nav className="flex h-full items-center justify-between px-2" aria-label="Global">
            {/* Pipeline selector */}
            <div
              className={clsx('flex flex-row items-center space-x-1', {
                hidden: !location.pathname.includes('/explore'),
              })}>
              {searchingForPipeline.current && !selectedPipelineOption ? (
                <Spinner />
              ) : selectedPipelineOption && !searchingForPipeline.current ? (
                <>
                  <Hierarchy1Icon className="h-4 w-4 stroke-gray-900 stroke-1" />
                  <Dropdown
                    options={pipelineOptions}
                    selectedOption={selectedPipelineOption}
                    onOptionSelect={handlePipelineSelection}
                    containerClassName="h-full w-full border border-gray-200 p-2 rounded-md"
                    buttonClassName="border-none w-64 -mr-1"
                    listClassName="max-h-36 overflow-y-auto w-64 max-w-64 top-11 left-0"
                    extraElement={
                      <p
                        className={clsx('cursor-default rounded-md px-2 py-1 text-xs font-medium', {
                          'bg-success-50 text-success-600': selectedPipeline?.enabled,
                          'bg-negative-50 text-negative-600': !selectedPipeline?.enabled,
                        })}>
                        {selectedPipeline?.enabled ? 'Enabled' : 'Disabled'}
                      </p>
                    }
                  />
                </>
              ) : (
                <>
                  <Hierarchy1Icon className="h-4 w-4 stroke-gray-900 stroke-1" />
                  <Dropdown
                    options={pipelineOptions}
                    selectedOption={null}
                    onOptionSelect={handlePipelineSelection}
                    containerClassName="h-full w-full border border-gray-200 p-2 rounded-md"
                    buttonClassName="border-none w-64 -mr-1"
                    listClassName="max-h-36 overflow-y-auto w-64 max-w-64 top-11 left-0"
                  />
                </>
              )}
            </div>

            {/* <div className="flex w-full items-center">
              {createNavBreadcrumb(matches).map((part, i) => (
                <div key={i}>{part}</div>
              ))}
            </div> */}
            {/* Leaving for future implementation */}
            {/* 
          <div className={'flex flex-1 justify-normal'}>{leftButtons}</div>
          <div className={'flex flex-1 justify-center'}>{centerButtons}</div>

          <div className={'flex flex-1 justify-end'}>{rightButtons}</div> */}
            <div
              className={clsx('flex h-full flex-row items-center justify-end space-x-4', {
                'w-full': !location.pathname.includes('/explore'),
                'w-2/5': location.pathname.includes('/explore'),
              })}>
              {otherOrganization && !auth.currentMember?.is_datable_admin && (
                <p
                  onClick={() => {
                    handleOrganizationChange(otherOrganization)
                  }}
                  className="cursor-pointer text-sm font-medium text-primary-500 transition-all hover:text-primary-700">
                  Go To Sandbox
                </p>
              )}
              {/* <div className="mr-4 flex flex-row space-x-4"> */}
              {/* <PipelineMenu pipelines={pipelines} /> */}
              {/* <InstanceSelector instances={instances} /> */}
              {/* </div> */}

              <UserMenu />
            </div>
          </nav>
        </header>
      )
    }
  }

  return renderActionBar()
}
